import { LocationDescriptorObject } from "history";
import React from "react";
import { useDropzone } from "react-dropzone";
import { useHistory } from "react-router";
import styled from "styled-components";

import { PrimaryButton } from "~/components/common/buttons";
import { blue, gray900, white } from "~/styles/theme/color";
import { borderRadius } from "~/styles/theme/common";
import { fromEvent } from "~/vendored/file-selector";

import {
  DocumentTree_InfoFragment,
  Document_BreadcrumbsFragment,
} from "../DocumentsAPI.generated";
import { View } from "../useDocumentsRouting";
import { useDocumentsUpload } from "./DocumentsUploadProvider";

export interface DocumentsUploadProps {
  tree: DocumentTree_InfoFragment;
  parent: Document_BreadcrumbsFragment;
  getLink: (documentId: string, view?: View) => LocationDescriptorObject;
}

export const DocumentsUploadNew = ({
  tree,
  parent,
  getLink,
}: DocumentsUploadProps) => {
  const uploadManager = useDocumentsUpload();
  const history = useHistory();

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    getFilesFromEvent: fromEvent,
    multiple: true,
    onDrop: (entries) => {
      uploadManager.queueUploads(tree, parent, entries);
      history.push(getLink(parent.id, "single"));
    },
  });

  return (
    <Root>
      <Dropzone {...getRootProps()} $isDragActive={isDragActive}>
        <input {...getInputProps()} />
        <DropzoneContent>
          <IconsContainer>
            <FileIcon />
            <FolderIcon />
          </IconsContainer>
          <DropzoneText>Drag and drop here</DropzoneText>
        </DropzoneContent>
      </Dropzone>

      <ButtonsContainer>
        <ImportButton onClick={open}>Import from your desktop</ImportButton>
      </ButtonsContainer>
    </Root>
  );
};

const Root = styled.div`
  display: flex;
  flex-direction: column;
`;

const Dropzone = styled.div<{ $isDragActive: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 20rem;
  margin-bottom: 1.5rem;
  border: 2px dashed ${(props) => (props.$isDragActive ? blue : "#ccc")};
  border-radius: ${borderRadius};
  background-color: ${(props) => (props.$isDragActive ? "#f0f7ff" : white)};
  cursor: pointer;
  transition: border-color 150ms ease-in-out, background-color 150ms ease-in-out;

  &:hover {
    border-color: ${blue};
    background-color: #f0f7ff;
  }
`;

const DropzoneContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
`;

const IconsContainer = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
`;

const FileIcon = styled.div`
  width: 3rem;
  height: 3.5rem;
  background-color: ${blue};
  border-radius: 0.25rem;
  position: relative;

  &::before {
    content: "";
    position: absolute;
    top: 0.5rem;
    left: 0.5rem;
    right: 0.5rem;
    height: 2px;
    background-color: ${white};
  }

  &::after {
    content: "";
    position: absolute;
    top: 1rem;
    left: 0.5rem;
    right: 0.5rem;
    height: 2px;
    background-color: ${white};
  }
`;

const FolderIcon = styled.div`
  width: 3.5rem;
  height: 3rem;
  background-color: ${blue};
  border-radius: 0.25rem;
  position: relative;

  &::before {
    content: "";
    position: absolute;
    top: -0.5rem;
    left: 0;
    width: 1.5rem;
    height: 0.5rem;
    background-color: ${blue};
    border-radius: 0.25rem 0.25rem 0 0;
  }
`;

const DropzoneText = styled.p`
  color: ${gray900};
  font-size: 0.875rem;
`;

const ButtonsContainer = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
`;

const ImportButton = styled(PrimaryButton)`
  height: 2.5rem;
  font-size: 1rem;
  padding: 0 1.5rem;
`;
